import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Search, Landmark, Building2, ChevronRight } from "lucide-react";

// Super admin → "Viewing as" from the Redline console: pick the portfolio or community a seat belongs
// to. `mode` = "company" (one portfolio) · "community" (one community). Lists are fetched WITHOUT
// preview headers so they're always complete.
const COPY = {
  principal: { title: "View as an Administrator", lead: "Pick the management company. You'll see their portfolio console exactly as its administrator does — every community they run, Team and Settings included." },
  pm: { title: "View as a Community Manager", lead: "Pick the management company first, then which of its communities this manager runs." },
  board: { title: "View as a Board member", lead: "Pick the community. You'll land inside it read-only, exactly as a board seat sees it." },
};

export function SeatPicker({ open, onOpenChange, seat, onChoose }) {
  const mode = seat === "principal" || seat === "pm" ? "company" : "community";
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (!open) return;
    setRows(null); setQ("");
    api.get(mode === "company" ? "/companies" : "/tenants", { headers: { "X-Preview-As": "" } })
      .then((r) => setRows((r.data || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""))))
      .catch(() => setRows([]));
  }, [open, mode]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (rows || []).filter((r) => !s || (r.name || "").toLowerCase().includes(s) || (r.persona_name || "").toLowerCase().includes(s));
  }, [rows, q]);
  const copy = COPY[seat] || COPY.board;
  const Icon = mode === "company" ? Landmark : Building2;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="seat-picker">
        <DialogHeader><DialogTitle className="font-heading">{copy.title}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#57534E] -mt-1">{copy.lead}</p>
        {rows && rows.length > 6 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/40" />
            <input data-testid="seat-picker-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" autoFocus
              className="w-full rounded-xl border border-sand-200 pl-9 pr-4 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
          </div>
        )}
        {!rows ? (
          <div className="flex items-center gap-2 text-sm text-[#8C827A] py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : list.length === 0 ? (
          <div className="text-sm text-[#8C827A] py-4" data-testid="seat-picker-empty">{rows.length ? "Nothing matches your search." : (mode === "company" ? "No portfolios yet." : "No communities yet.")}</div>
        ) : (
          <div className="max-h-72 overflow-y-auto rounded-xl border border-sand-200 divide-y divide-sand-100" data-testid="seat-picker-list">
            {list.map((r) => (
              <button key={r.id} type="button" data-testid={`seat-pick-${r.id}`} onClick={() => onChoose(r)}
                className="w-full flex items-center gap-3 px-3.5 py-2.5 text-sm text-left hover:bg-sand-50 transition-colors">
                <Icon className="h-4 w-4 text-ink/35 shrink-0" strokeWidth={1.75} />
                <span className="flex-1 min-w-0">
                  <span className="block truncate font-semibold text-[#1C1917]">{r.name}</span>
                  <span className="block truncate text-xs text-[#8C827A]">
                    {mode === "company" ? `${r.communities ?? 0} ${r.communities === 1 ? "community" : "communities"}` : (r.company_name || "Not in a portfolio")}
                  </span>
                </span>
                <ChevronRight className="h-4 w-4 text-ink/30 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
